/**
 * SixtySecondHero — V145.
 *
 * Inspired by high-converting FB-ad creatives (Scalp AI / Smart Helper
 * pattern) — but stripped of the scammy "earn 1-4% daily" claims.
 *
 * Structure that converts:
 *   ⚡ Big friction-reversal headline ("Too easy not to try")
 *   3-icon value stack (ROI / Mechanism / Quality)
 *   Phone mockup showing actual product
 *   ONE dominant START button (Telegram-icon style)
 *   "Start in 2 minutes" reassurance below
 *
 * This sits ABOVE the existing hero — it's the scroll-stopper for paid
 * traffic. Existing hero stays for organic / direct visitors who want
 * the full pitch.
 */
import { Link } from "react-router-dom";
import { ArrowRight, Zap, TrendingUp, ShieldCheck, Sparkles } from "lucide-react";
import { useLanguage } from "../../contexts/LanguageContext";
import { funnelT } from "../../translations-funnel";

export default function SixtySecondHero({ liveCount, soonCount, totalSavingsEur }) {
  const { language } = useLanguage();
  const ft = funnelT(language);

  return (
    <section
      data-testid="60s-hero"
      className="relative px-4 sm:px-6 py-12 sm:py-20 border-b border-amber-500/30 overflow-hidden"
      style={{
        background: "radial-gradient(ellipse at top, rgba(245, 158, 11, 0.10) 0%, #0a0a0a 60%)",
      }}
    >
      <div className="max-w-6xl mx-auto relative z-10">
        {/* Eyebrow with energy emoji — pattern interrupt */}
        <p className="text-[10px] sm:text-[11px] uppercase tracking-[0.3em] font-black text-amber-400 mb-4 text-center">
          ⚡ ⚡ ⚡
        </p>

        {/* Friction-reversal headline */}
        <h1 className="text-4xl sm:text-6xl lg:text-7xl font-black text-center leading-[1.05] mb-3">
          <span className="text-white">{ft.sxs_h1_a}</span>{" "}
          <span style={{ color: "#A3E635", textShadow: "0 0 24px rgba(163, 230, 53, 0.4)" }}>
            {ft.sxs_h1_b}
          </span>
        </h1>
        <p className="text-center text-base sm:text-lg text-gray-300 max-w-3xl mx-auto mb-10 sm:mb-14 leading-relaxed">
          <ShieldCheck className="w-4 h-4 inline mr-1.5 text-amber-400" />
          {ft.sxs_subhead}
        </p>

        {/* 3-column value stack (left) + Phone mockup (right) */}
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-8 lg:gap-12 items-center max-w-5xl mx-auto mb-10">
          {/* Left: 3 value props */}
          <div className="space-y-4">
            <ValueRow
              icon={<TrendingUp className="w-6 h-6" style={{ color: "#A3E635" }} />}
              eyebrow={ft.sxs_val1_eyebrow}
              title={`€${totalSavingsEur.toLocaleString("sk-SK")}/${ft.sxs_val1_unit}`}
              testid="sxs-val-roi"
            />
            <ValueRow
              icon={<Zap className="w-6 h-6" style={{ color: "#A3E635" }} />}
              eyebrow={ft.sxs_val2_eyebrow}
              title={ft.sxs_val2_title}
              testid="sxs-val-auto"
            />
            <ValueRow
              icon={<Sparkles className="w-6 h-6" style={{ color: "#A3E635" }} />}
              eyebrow={ft.sxs_val3_eyebrow}
              title={`${liveCount} ${ft.sxs_val3_suffix} · +${soonCount}`}
              testid="sxs-val-agents"
            />
          </div>

          {/* Right: CSS-only phone mockup with our actual product preview */}
          <div className="flex justify-center">
            <PhoneMockup ft={ft} />
          </div>
        </div>

        {/* Dominant START button */}
        <div className="text-center">
          <Link
            to="/agent-os/onboard"
            data-testid="60s-hero-start-cta"
            data-cta
            className="inline-flex items-center gap-2 sm:gap-3 bg-gradient-to-r from-amber-400 to-amber-500 text-black font-black px-8 sm:px-16 py-4 sm:py-5 text-base sm:text-xl uppercase tracking-[0.2em] hover:from-amber-300 hover:to-amber-400 transition-all shadow-[0_0_40px_rgba(245,158,11,0.4)]"
          >
            <Zap className="w-5 h-5 sm:w-6 sm:h-6" />
            {ft.sxs_cta_button}
            <ArrowRight className="w-5 h-5 sm:w-6 sm:h-6" />
          </Link>
          <p className="text-xs sm:text-sm text-gray-400 mt-4 flex items-center justify-center gap-1.5">
            <Zap className="w-3.5 h-3.5 text-amber-400" />
            {ft.sxs_cta_subtext}
          </p>
          {/* V146 — secondary CTA: free personalized scan */}
          <Link
            to="/agent-match"
            data-testid="60s-hero-scan-cta"
            className="inline-flex items-center gap-1.5 mt-5 text-sm text-lime-300 hover:text-lime-200 underline underline-offset-4 decoration-lime-500/40"
          >
            <Sparkles className="w-3.5 h-3.5" />
            {ft.sxs_scan_cta || "Or get a free personalized AI scan for YOUR site →"}
          </Link>
        </div>
      </div>
    </section>
  );
}

function ValueRow({ icon, eyebrow, title, testid }) {
  return (
    <div
      data-testid={testid}
      className="border border-gray-800 bg-black/40 p-4 sm:p-5 flex items-center gap-4 hover:border-amber-500/40 transition-colors"
    >
      <div className="flex-shrink-0">{icon}</div>
      <div className="min-w-0">
        <p className="text-[10px] uppercase tracking-widest font-black text-gray-400">{eyebrow}</p>
        <p className="text-xl sm:text-2xl font-black text-white leading-tight">{title}</p>
      </div>
    </div>
  );
}

function PhoneMockup({ ft }) {
  return (
    <div className="relative w-[240px] sm:w-[280px]">
      {/* Phone frame */}
      <div className="rounded-[36px] border-[10px] border-neutral-800 bg-black overflow-hidden shadow-[0_0_60px_rgba(163,230,53,0.25)]">
        {/* Status bar */}
        <div className="bg-black px-4 py-2 flex justify-between items-center text-[9px] text-gray-300 font-mono">
          <span>16:42</span>
          <span>● ● ●</span>
        </div>
        {/* Header */}
        <div className="bg-neutral-900 px-3 py-2 border-b border-gray-800 flex items-center gap-2">
          <div className="w-7 h-7 rounded-full bg-amber-500 flex items-center justify-center text-black text-xs font-black">A</div>
          <div className="flex-1">
            <p className="text-[10px] text-white font-bold">Agent OS</p>
            <p className="text-[8px] text-gray-500">{ft.sxs_phone_subtitle}</p>
          </div>
        </div>
        {/* Body — pulsing AI chart visual */}
        <div className="px-4 py-6 text-center">
          <p className="text-[9px] uppercase tracking-widest text-amber-400 font-black mb-2">
            {ft.sxs_phone_eyebrow}
          </p>
          <p className="text-2xl font-black mb-1" style={{ color: "#A3E635" }}>
            {ft.sxs_phone_welcome}
          </p>
          <p className="text-[10px] text-gray-400 leading-relaxed mb-4">
            {ft.sxs_phone_descline}
          </p>
          {/* Mini chart visual */}
          <div className="flex items-end justify-center gap-1 h-12 mb-4">
            {[3, 5, 4, 7, 6, 9, 8, 11, 10, 12].map((h, i) => (
              <div
                key={i}
                className="w-1.5 rounded-t"
                style={{
                  height: `${h * 4}px`,
                  background: i === 9 ? "#A3E635" : "rgba(163, 230, 53, 0.4)",
                  boxShadow: i === 9 ? "0 0 8px #A3E635" : "none",
                }}
              />
            ))}
          </div>
          {/* Mini CTA */}
          <div className="bg-black border border-amber-500/40 px-3 py-2">
            <p className="text-[9px] text-amber-300 font-mono">{ft.sxs_phone_launch}</p>
          </div>
        </div>
      </div>
      {/* Floating "60s" badge */}
      <div className="absolute -top-3 -right-3 bg-amber-500 text-black font-black text-xs px-3 py-1.5 rotate-[8deg] shadow-lg">
        60s
      </div>
    </div>
  );
}
